import React, { useCallback, useEffect, useState } from 'react';
import Box from 'apollo-react/components/Box';
import CircularProgress from 'apollo-react/components/CircularProgress';
import Typography from 'apollo-react/components/Typography';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  postEndUserPasswordVerify,
  postEndUserPasswordChange,
} from './endUserProfileApi';
import { useHcpProfileModule } from '../hcpProfile/useHcpProfileModule';
import ProfileHeaderBar from '../hcpProfile/components/ProfileHeaderBar';
import GeneralInformationTab from '../hcpProfile/tabs/GeneralInformationTab';
import LicenseInformationTab from '../hcpProfile/tabs/LicenseInformationTab';
import EducationExperienceTab from '../hcpProfile/tabs/EducationExperienceTab';
import ProfileCommon from './profileUi/ProfileCommon';
import PasswordSection from './profileUi/sections/PasswordSection';
import styles from './profileUi/Profile.module.css';
import { useLicensingExperienceOptional } from '../../contexts/LicensingExperienceContext';

function clampTabIndex(i, maxIdx) {
  const n = Number(i);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(maxIdx, Math.floor(n)));
}

/**
 * Customer profile: HCP maintenance (personal, address, licenses, education) plus password.
 */
export default function CustomerProfilePage({ initialTabIndex = 0 }) {
  const { t } = useTranslation();
  const { mode } = useLicensingExperienceOptional();
  const showFullHcpProfile = mode === 'licensing_tool';
  const maxTabIndex = showFullHcpProfile ? 4 : 1;
  const initial = clampTabIndex(initialTabIndex, maxTabIndex);

  const [passwordVerified, setPasswordVerified] = useState(false);

  const {
    profileData,
    loading: hcpLoading,
    errorMessage,
    refreshProfile,
  } = useHcpProfileModule();

  const [activeTab, setActiveTab] = useState(initial);
  const [mountedTabs, setMountedTabs] = useState(() => new Set([initial]));

  useEffect(() => {
    setActiveTab(clampTabIndex(initialTabIndex, maxTabIndex));
  }, [initialTabIndex, maxTabIndex]);

  useEffect(() => {
    if (!showFullHcpProfile && activeTab > 1) {
      setActiveTab(1);
    }
  }, [showFullHcpProfile, activeTab]);

  useEffect(() => {
    setMountedTabs((prev) => new Set([...prev, activeTab]));
  }, [activeTab]);

  const handleTabChange = useCallback((_, value) => {
    setActiveTab(value);
  }, []);

  const handleVerifyPassword = useCallback(
    async (currentPassword) => {
      try {
        await postEndUserPasswordVerify(currentPassword);
        setPasswordVerified(true);
        toast.success(t('profileData.verified', { defaultValue: 'Verified' }));
      } catch (e) {
        setPasswordVerified(false);
        toast.error(
          e?.response?.data?.message ||
            t('profileData.verifyFailed', {
              defaultValue: 'Verification failed',
            })
        );
      }
    },
    [t]
  );

  const handleChangePassword = useCallback(
    async (currentPassword, newPassword) => {
      try {
        await postEndUserPasswordChange(currentPassword, newPassword);
        toast.success(
          t('profileData.passwordChanged', { defaultValue: 'Password changed' })
        );
        setPasswordVerified(false);
        window.dispatchEvent(new Event('profile-updated'));
      } catch (e) {
        toast.error(
          e?.response?.data?.message ||
            t('profileData.passwordChangeError', {
              defaultValue: 'Could not change password',
            })
        );
      }
    },
    [t]
  );

  const loading = hcpLoading && !profileData;

  if (loading) {
    return (
      <Box textAlign="center" py={5}>
        <CircularProgress />
        <Typography variant="body" color="text.secondary" sx={{ mt: 2 }}>
          {t('loading', { defaultValue: 'Loading…' })}
        </Typography>
      </Box>
    );
  }

  if (errorMessage && !profileData) {
    return (
      <Box sx={{ mt: 4, width: '100%' }}>
        <Box
          sx={{
            p: 2,
            borderRadius: 1,
            border: '1px solid #e0c36a',
            backgroundColor: '#fffbf0',
          }}
        >
          <Typography variant="body2">{errorMessage}</Typography>
        </Box>
      </Box>
    );
  }

  if (!profileData) {
    return (
      <Box sx={{ mt: 4, width: '100%' }}>
        <Box
          sx={{
            p: 2,
            borderRadius: 1,
            border: '1px solid #e0c36a',
            backgroundColor: '#fffbf0',
          }}
        >
          <Typography variant="body2">
            {t('profileData.loadError', {
              defaultValue: 'Could not load your profile.',
            })}
          </Typography>
        </Box>
      </Box>
    );
  }

  const data = profileData;

  const tabPropsBase = {
    segment: data.general,
    lockedPaths: data.lockedGeneralPaths,
    lockMessages: data.generalLockMessages,
    verificationLockedPaths:
      data.profileFieldPermissions?.verificationLockedPaths,
    validationConfig: data.validationConfig,
    attachmentRevisions: data.attachmentRevisions || [],
    onSaved: refreshProfile,
    showTitle: false,
    /** Match monolith `/admin/profile` label/value rows inside tab content cards */
    cardFieldLayout: true,
  };

  const personalContent = (
    <div className={styles.profileFieldWidth}>
      <GeneralInformationTab
        variant="personal"
        accountFieldsOnly={!showFullHcpProfile}
        {...tabPropsBase}
      />
    </div>
  );

  const addressContent = (
    <div className={styles.profileFieldWidth}>
      <GeneralInformationTab variant="address" {...tabPropsBase} />
    </div>
  );

  const licensesContent = (
    <div className={styles.profileFieldWidth}>
      <LicenseInformationTab
        licenses={data.licenses}
        licenseFieldConfig={data.licenseFieldConfig}
        licenseRepeatableLimits={data.licenseRepeatableLimits}
        attachmentRevisions={data.attachmentRevisions || []}
        onSaved={refreshProfile}
      />
    </div>
  );

  const educationContent = (
    <div className={styles.profileFieldWidth}>
      <EducationExperienceTab
        educations={data.educations}
        experience={data.experience}
        educationRepeatableLimits={data.educationRepeatableLimits}
        attachmentRevisions={data.attachmentRevisions || []}
        onSaved={refreshProfile}
        showPageTitle={false}
      />
    </div>
  );

  const passwordContent = (
    <div className={styles.profileFieldWidth}>
      <PasswordSection
        verified={passwordVerified}
        requireCurrentPassword
        onVerifyPassword={handleVerifyPassword}
        onChangePassword={handleChangePassword}
        onCancel={() => setPasswordVerified(false)}
      />
    </div>
  );

  const passwordLabel = t('profileData.password', { defaultValue: 'Password' });

  const tabs = showFullHcpProfile
    ? [
        'Personal Information',
        'Address Information',
        'License Information',
        'Education and Experience',
        passwordLabel,
      ]
    : ['Personal Information', passwordLabel];

  const tabContents = showFullHcpProfile
    ? [
        personalContent,
        addressContent,
        licensesContent,
        educationContent,
        passwordContent,
      ]
    : [personalContent, passwordContent];

  const sections = [
    {
      title: tabs[activeTab],
      content: (
        <>
          {tabContents.map((content, idx) =>
            mountedTabs.has(idx) ? (
              <div
                key={idx}
                style={{ display: activeTab === idx ? 'block' : 'none' }}
                aria-hidden={activeTab !== idx}
              >
                {content}
              </div>
            ) : null
          )}
        </>
      ),
    },
  ];

  return (
    <Box className={styles.customerProfileModule} sx={{ mt: 4, width: '100%' }}>
      <ProfileHeaderBar
        publicHcpId={data.publicHcpId}
        submittedAt={data.submittedAt}
        profileLastUpdatedAt={data.profileLastUpdatedAt}
      />

      <ProfileCommon
        wrapMonolithContainer={false}
        tabsVariant="scrollable"
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        sections={sections}
        hideSectionTitle
      />
    </Box>
  );
}
